import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  CheckCircle as ResolvedIcon,
  Warning as PendingIcon,
  Error as EscalatedIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import axios from 'axios';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const AdminPOSHManagement = () => {
  const { getAuthHeaders } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    status: 'Pending',
    priority: 'Medium',
    assigned_to: '',
    internal_committee_notes: '',
    resolution_notes: ''
  });

  // Fetch all POSH complaints
  const fetchComplaints = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/posh/complaints`,
        { headers: getAuthHeaders() }
      );
      setComplaints(response.data.complaints || response.data || []);
    } catch (error) {
      console.error('Error fetching POSH complaints:', error);
      toast.error('Failed to fetch POSH complaints');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComplaints();
  }, []);

  // Handle view complaint details
  const handleViewComplaint = (complaint) => {
    setSelectedComplaint(complaint);
    setEditMode(false);
    setFormData({
      status: complaint.status || 'Pending',
      priority: complaint.priority || 'Medium',
      assigned_to: complaint.assigned_to || '',
      internal_committee_notes: complaint.internal_committee_notes || '',
      resolution_notes: complaint.resolution_notes || ''
    });
    setDialogOpen(true);
  };

  // Handle edit complaint
  const handleEditComplaint = (complaint) => {
    setSelectedComplaint(complaint);
    setEditMode(true);
    setFormData({
      status: complaint.status || 'Pending',
      priority: complaint.priority || 'Medium',
      assigned_to: complaint.assigned_to || '',
      internal_committee_notes: complaint.internal_committee_notes || '',
      resolution_notes: complaint.resolution_notes || ''
    });
    setDialogOpen(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const updateData = {
        ...formData,
        poshId: selectedComplaint.poshId // Include poshId for soft delete
      };

      const response = await axios.put(
        `${API_URL}/posh/complaint/${selectedComplaint._id}/status`,
        updateData,
        { headers: getAuthHeaders() }
      );
      
      toast.success('POSH complaint updated successfully');
      setDialogOpen(false);
      fetchComplaints(); // Refresh the list
    } catch (error) {
      console.error('Error updating POSH complaint:', error);
      toast.error(error.response?.data?.error || 'Failed to update POSH complaint');
    }
  };

  // Download supporting document
  const downloadDocument = async (complaintId, fileName, originalName) => {
    try {
      const response = await axios.get(
        `${API_URL}/posh/complaint/${complaintId}/document/${fileName}`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      link.style.display = 'none';
      link.id = `download-link-${Date.now()}`; // Unique ID
      
      // Add to DOM and trigger download
      document.body.appendChild(link);
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        link.click();
        
        // Clean up after a short delay
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      });
    } catch (error) {
      console.error('Error downloading document:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error.response?.status === 404) {
        toast.error('Document not found');
      } else {
        toast.error('Failed to download document');
      }
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Resolved': return 'success';
      case 'Pending': return 'warning';
      case 'Under Investigation': return 'info';
      case 'Escalated': return 'error';
      case 'Closed': return 'default';
      default: return 'default';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="600" sx={{ fontSize: '30px' ,lineHeight:1.5 ,letterSpacing:1 }}>
          POSH Complaint Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchComplaints}
        >
          Refresh
        </Button>
      </Box>

      {complaints.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '20px' }}>
            No POSH Complaints Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
            There are currently no POSH complaints in the system.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>POSH ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Complainant</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Harasser</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Incident Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>POSH case</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {complaints.map((complaint) => (
                <TableRow key={complaint._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {complaint.poshId || `#${complaint._id.slice(-6)}`}
                    </Typography>
                  </TableCell>
                                     <TableCell>
                     <Typography variant="body2">
                       {complaint.anonymous ? 'Anonymous' : complaint.name || 'N/A'}
                     </Typography>
                     <Typography variant="caption" color="text.secondary">
                       {complaint.anonymous ? 'Anonymous Complaint' : complaint.email || 'N/A'}
                     </Typography>
                   </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {complaint.harasser_name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {complaint.type_of_incident || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {complaint.date_of_incident ? formatDate(complaint.date_of_incident) : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={complaint.status || 'Pending'}
                      color={getStatusColor(complaint.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(complaint.priority?.toLowerCase() === 'high' || complaint.priority?.toLowerCase() === 'critical') ? 'Yes' : 'No'}
                      color={(complaint.priority?.toLowerCase() === 'high' || complaint.priority?.toLowerCase() === 'critical') ? 'error' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewComplaint(complaint)}
                          color="primary"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Update Status">
                        <IconButton
                          size="small"
                          onClick={() => handleEditComplaint(complaint)}
                          color="secondary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Update POSH Complaint Status' : 'POSH Complaint Details'}
        </DialogTitle>
        <DialogContent>
          {selectedComplaint && (
            <Box sx={{ mt: 2 }}>
              {/* Complaint Details */}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', fontSize: '20px' }}>
                    Basic Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>POSH ID:</strong> {selectedComplaint.poshId || `#${selectedComplaint._id.slice(-6)}`}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Submitted:</strong> {formatDate(selectedComplaint.created_at)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Current Status:</strong> 
                    <Chip 
                      label={selectedComplaint.status || 'Pending'} 
                      color={getStatusColor(selectedComplaint.status)} 
                      size="small" 
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>POSH case:</strong>
                    <Chip 
                      label={(selectedComplaint.priority?.toLowerCase() === 'high' || selectedComplaint.priority?.toLowerCase() === 'critical') ? 'Yes' : 'No'} 
                      color={(selectedComplaint.priority?.toLowerCase() === 'high' || selectedComplaint.priority?.toLowerCase() === 'critical') ? 'error' : 'success'} 
                      size="small" 
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </Grid>
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3 }}>
                    Complainant's Details
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Submission Type:</strong> {selectedComplaint.anonymous ? 'Anonymous' : 'Self'}
                  </Typography>
                </Grid>
                
                {!selectedComplaint.anonymous && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Name:</strong> {selectedComplaint.name || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Employee ID:</strong> {selectedComplaint.employee_id || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Department:</strong> {selectedComplaint.department || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Designation:</strong> {selectedComplaint.designation || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Contact Number:</strong> {selectedComplaint.contact_number || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Email Address:</strong> {selectedComplaint.email || 'N/A'}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Are you comfortable being contacted by the Internal Committee?</strong> {selectedComplaint.comfortable_contact || 'N/A'}
                  </Typography>
                </Grid>
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3 }}>
                    Details of the Alleged Harasser
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    (Person against whom the complaint is being made)
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Name:</strong> {selectedComplaint.harasser_name || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Department:</strong> {selectedComplaint.harasser_department || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Designation:</strong> {selectedComplaint.harasser_designation || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Do you work directly with this person for your regular work activities?</strong> {selectedComplaint.direct_work_relation || 'N/A'}
                  </Typography>
                </Grid>
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3 }}>
                    Incident Details
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Type of Incident (physical/verbal/visual/online):</strong> {selectedComplaint.type_of_incident || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Location of Incident:</strong> {selectedComplaint.location_of_incident || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Date of Incident:</strong> {selectedComplaint.date_of_incident ? formatDate(selectedComplaint.date_of_incident) : 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Time of Incident:</strong> {selectedComplaint.time_of_incident || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Is this the first occurrence?</strong> {selectedComplaint.first_occurrence || 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.first_occurrence === 'no' && selectedComplaint.previous_incidents && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>If No, provide details of previous incidents:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.previous_incidents}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Were there any witnesses?</strong> {selectedComplaint.witnesses || 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.witnesses === 'yes' && selectedComplaint.witnesses_details && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>If yes, please provide names/contact info:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.witnesses_details}
                    </Typography>
                  </Grid>
                )}
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3 }}>
                    Incident Description
                  </Typography>
                  {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please describe the incident in detail. Mention the sequence of events, exact words or actions, and any context you feel is important. 
                    Please describe the behaviour / action(s) in detail. Include facts to the best of your knowledge.
                  </Typography> */}
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Incident Description:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {selectedComplaint.incident_description || 'No description provided'}
                  </Typography>
                </Grid>
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3 }}>
                    Supporting Evidence (if any)
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Documents, emails, text messages, screenshots, etc. attached?</strong> {selectedComplaint.has_supporting_evidence || 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.has_supporting_evidence === 'yes' && selectedComplaint.supporting_evidence && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>If yes, list or describe them briefly:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.supporting_evidence}
                    </Typography>
                  </Grid>
                )}
                
                                 {/* Supporting Documents */}
                 {selectedComplaint.supporting_documents && 
                  selectedComplaint.supporting_documents.length > 0 && 
                  selectedComplaint.supporting_documents.some(doc => doc.fileName && doc.originalName) && (
                   <Grid item xs={12}>
                     <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                       Supporting Documents ({selectedComplaint.supporting_documents.filter(doc => doc.fileName && doc.originalName).length}):
                     </Typography>
                     <List dense>
                       {selectedComplaint.supporting_documents
                         .filter(doc => doc.fileName && doc.originalName)
                         .map((doc, index) => (
                         <ListItem key={index} sx={{ py: 0.5 }}>
                           <AttachFileIcon sx={{ mr: 1, color: 'primary.main', fontSize: 16 }} />
                           <ListItemText
                             primary={doc.originalName}
                             secondary={doc.fileSize ? `${(doc.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Size not available'}
                             sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                           />
                           <ListItemSecondaryAction>
                             <IconButton
                               edge="end"
                               aria-label="download"
                               onClick={() => downloadDocument(selectedComplaint._id, doc.fileName, doc.originalName)}
                               color="primary"
                               size="small"
                             >
                               <DownloadIcon />
                             </IconButton>
                           </ListItemSecondaryAction>
                         </ListItem>
                       ))}
                     </List>
                   </Grid>
                 )}
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3 }}>
                    Actions Already Taken (if any)
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Did you inform anyone about the incident?</strong> {selectedComplaint.informed_someone || 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.informed_someone === 'yes' && selectedComplaint.who_informed && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>If yes, who did you inform and when?</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.who_informed}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Have any steps been taken (formally/informally) to resolve the matter?</strong> {selectedComplaint.steps_taken_flag || 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.steps_taken_flag === 'yes' && selectedComplaint.steps_taken_details && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>If yes, please explain:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.steps_taken_details}
                    </Typography>
                  </Grid>
                )}
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3 }}>
                    Preferred Outcome or Resolution
                  </Typography>
                </Grid>
                
                {selectedComplaint.preferred_resolution && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Do you have any preferences regarding how this should be resolved or addressed?</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.preferred_resolution}
                    </Typography>
                  </Grid>
                )}
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3 }}>
                    Reporting Details
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Date of Filing the Complaint:</strong> {selectedComplaint.date_of_filing ? formatDate(selectedComplaint.date_of_filing) : 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.reason_for_delay && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Reason for delay in filing (if beyond 3 months):</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.reason_for_delay}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Is your Manager/HR informed about the incident?</strong> {selectedComplaint.manager_informed || 'N/A'}
                  </Typography>
                </Grid>

              </Grid>

              {/* Action Taken History - Show in both view and edit modes */}
              {selectedComplaint.actionTakenHistory && selectedComplaint.actionTakenHistory.length > 0 && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontSize: '18px' }}>
                      Action Taken History
                    </Typography>
                    <Box sx={{ 
                      maxHeight: '400px', 
                      overflowY: 'auto',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'grey.50'
                    }}>
                      {selectedComplaint.actionTakenHistory.map((action, index) => (
                        <Box key={index} sx={{ mb: 3 }}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: action.deletedAt ? 'grey.50' : 'info.50', 
                            borderRadius: 2, 
                            border: '1px solid', 
                            borderColor: action.deletedAt ? 'grey.300' : 'info.300',
                            opacity: action.deletedAt ? 0.8 : 1,
                            boxShadow: action.deletedAt ? 'none' : '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Chip
                                  label={action.status}
                                  color={action.status === 'Resolved' ? 'success' : action.status === 'Escalated' ? 'error' : 'warning'}
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                <Chip
                                  label={action.priority}
                                  color={action.priority === 'Critical' ? 'error' : action.priority === 'High' ? 'warning' : 'info'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                {action.deletedAt && (
                                  <Chip
                                    label="Previous Update"
                                    color="default"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              <Typography variant="caption" sx={{ 
                                color: 'text.secondary',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}>
                                {formatDateTime(action.created_at)}
                              </Typography>
                            </Box>
                            {action.internal_committee_notes && (
                              <Typography variant="body2" sx={{ 
                                mt: 1,
                                lineHeight: 1.6,
                                color: 'text.primary'
                              }}>
                                <strong>Internal Committee Notes:</strong> {action.internal_committee_notes}
                              </Typography>
                            )}
                            {action.resolution_notes && (
                              <Typography variant="body2" sx={{ 
                                mt: 1,
                                lineHeight: 1.6,
                                color: 'text.primary'
                              }}>
                                <strong>Resolution Notes:</strong> {action.resolution_notes}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {editMode && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                        Update Status
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          label="Status"
                        >
                          <MenuItem value="Pending">Pending</MenuItem>
                          <MenuItem value="Under Investigation">Under Investigation</MenuItem>
                          <MenuItem value="Resolved">Resolved</MenuItem>
                          <MenuItem value="Escalated">Escalated</MenuItem>
                          <MenuItem value="Closed">Closed</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>POSH case</InputLabel>
                        <Select
                          value={(formData.priority?.toLowerCase() === 'high' || formData.priority?.toLowerCase() === 'critical') ? 'Yes' : 'No'}
                          onChange={(e) => setFormData({ ...formData, priority: e.target.value === 'Yes' ? 'High' : 'Low' })}
                          label="POSH case"
                        >
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Internal Committee Notes"
                        value={formData.internal_committee_notes}
                        onChange={(e) => setFormData({ ...formData, internal_committee_notes: e.target.value })}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Resolution Notes"
                        value={formData.resolution_notes}
                        onChange={(e) => setFormData({ ...formData, resolution_notes: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Close
          </Button>
          {editMode && (
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Update Status
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPOSHManagement;
